import bottomarrowtbl from "../../assets/dynamicTable/arrow-bottom.svg";
import sortArrowTopIcon from "../../assets/dynamicTable/arrow-top.svg";
import arrowLeftCircle from "../../assets/dynamicTable/arrow-left-circle.svg";
import arrowRightCircle from "../../assets/dynamicTable/arrow-right-circle.svg";
import sortIcon from "../../assets//dynamicTable/sorticons.svg";
import React, { useState, useEffect, useRef } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, flexRender, SortingState } from "@tanstack/react-table";
import { format } from "date-fns";
import PaginationNew from "./PaginationNew";

type MyComponentProps = {
    filteredData: any;
    selectedColumns: any;
    sorting: SortingState;
    setSorting: any;
    selectedRows: Set<string>;
    setSelectedRows: any;
    setMainTable?: any;
    showAllCheckbox?: any;
    currentPage?: any;
    totalPages?: any;
    handlePageChange?: any;
    setMainTableUpdate?: any;
    showPagination?: any;
    dottedPagination?: boolean;
    notFoundMessage?: any;
    type?: any;
    paginationDataLength?: any;
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
    serialNumber?: boolean;
        notFoundBodyClass?: string; //
};

const DynamicTable: React.FC<MyComponentProps> = ({
    filteredData,
    selectedColumns,
    sorting,
    setSorting,
    selectedRows,
    setSelectedRows,
    setMainTable,
    showAllCheckbox,
    currentPage,
    totalPages,
    handlePageChange,
    setMainTableUpdate,
    showPagination,
    dottedPagination,
    notFoundMessage,
    type,
    paginationDataLength,
    pageSize,
    onPageSizeChange,
    serialNumber,
    notFoundBodyClass
}) => {
    // State to store filter values
    const [filterValues, setFilterValues] = useState<{ [key: string]: string }>({});
    const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null); // Track which column filter is active
    const [columnOrder, setColumnOrder] = useState<any[]>(selectedColumns.map((col: any) => col.id)); // Initialize column order

    const table = useReactTable({
        data: filteredData,
        columns: selectedColumns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(), // Add filtering capabilities
        state: { sorting, columnOrder },
        onSortingChange: setSorting,
        onColumnOrderChange: setColumnOrder,
    });

    const hasNoData = table.getRowModel()?.rows?.length === 0;

    // Use useEffect to sync columnOrder with selectedColumns when it changes
    useEffect(() => {
        // Check if selectedColumns have changed, and update columnOrder accordingly
        if (selectedColumns?.length > 0) {
            setColumnOrder(selectedColumns.map((col: any) => col.id));
        }
    }, [selectedColumns]); // Add selectedColumns as a dependency

    useEffect(() => {
        if (setMainTable) {
            setMainTable(table);
            setMainTableUpdate((prev: any) => !prev);
        }
    }, [table]);

    // Handle filter value change
    const handleFilterChange = (columnId: string, value: string) => {
        setFilterValues((prev) => ({
            ...prev,
            [columnId]: value,
        }));
        table.getColumn(columnId)?.setFilterValue(value); // Apply the filter
    };

    // Close filter popup if user clicks outside
    const filterRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setActiveFilterColumn(null); // Close the filter popup
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSelectAll = (checked: boolean) => {
        setSelectedRows((prev: any) => {
            const newSet = new Set<string>(prev);
            table.getRowModel().rows.forEach((row: any) => {
                if (checked) {
                    newSet.add(row.id);
                } else {
                    newSet.delete(row.id);
                }
            });
            return newSet;
        });
    };

    const handleRowSelect = (rowId: string) => {
        setSelectedRows((prev: any) => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    const handleDragStart = (e: React.DragEvent, columnId: string) => {
        e.dataTransfer.setData("text/plain", columnId);
    };

    const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
        const sourceColumnId = e.dataTransfer.getData("text/plain");

        if (sourceColumnId !== targetColumnId) {
            const newColumnOrder = [...columnOrder];
            const sourceIndex = newColumnOrder.indexOf(sourceColumnId);
            const targetIndex = newColumnOrder.indexOf(targetColumnId);

            newColumnOrder.splice(sourceIndex, 1); // Remove source column
            newColumnOrder.splice(targetIndex, 0, sourceColumnId); // Insert at target position

            setColumnOrder(newColumnOrder); // Update column order
        }
    };

    const isAnyFilterActive = Object.values(filterValues).some((value) => value.trim() !== "");

    const renderCellContent = (cell: any) => {
        const columnId = cell.column.id;
        const cellValue = cell.getValue();

        // Let the column's cell renderer handle the formatting if it exists
        if (cell.column.columnDef.cell && typeof cell.column.columnDef.cell === "function") {
            return flexRender(cell.column.columnDef.cell, cell.getContext());
        }

        // For other date columns that don't have a custom cell renderer
        if (columnId.toLowerCase()?.includes("date") && cellValue) {
            try {
                return format(new Date(cellValue), "MM-dd-yyyy hh:mm a");
            } catch (error) {
                return cellValue;
            }
        }

        // Check if the column name is 'bill_hours', 'pay_hours', or 'caregiver_bill_hours' and append 'hrs'
        if (["bill_hours", "pay_hours", "caregiver_bill_hours"]?.includes(columnId)) {
            return cellValue ? `${cellValue} hrs` : "-";
        } else if (["billable_amount, payroll_amount", "total_bill"]?.includes(columnId)) {
            return cellValue ? `$${cellValue}` : "-";
        }

        return cellValue;
    };

    const isDraggable = (header: any) => {
        return header && header.column && header.column.columnDef && !header.column.columnDef.disableDragging;
    };

    const handleStartDrag = (e: React.DragEvent, header: any) => {
        if (isDraggable(header)) {
            handleDragStart(e, header.id);
        }
    };
    const canDragOver = (header: any) => {
        return header && header.column && header.column.columnDef && !header.column.columnDef.disableDragging;
    };

    const handleDragOver = (e: React.DragEvent, header: any) => {
        if (canDragOver(header)) {
            e.preventDefault(); // Allow drop only for draggable columns
        }
    };

    const handleHeaderClick = (e: any, header: any) => {
        const classNames = ["content-dropdown-wrap-dv", "input-dropdown-dynamic-table"];
        const classesPresent = classNames?.some((className) => e.target.classList.contains(className));
        //Above code for dont show search onclick on header if any dropdown or box is added
        if (header.column.getCanFilter() && !classesPresent) {
            setActiveFilterColumn(header.id);
        }
    };

    const isNumericColumn = (column: any, headerId: string) => {
        const rowModel = column?.getFacetedRowModel();
        if (!rowModel) return false;

        return rowModel.rows.every((row: any) => {
            const value = row.getValue(headerId);
            return !isNaN(Number(value));
        });
    };

    const isAllowedKey = (key: string, isNumeric: boolean) => {
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
        const isNumber = /[0-9]/.test(key);

        if (!isNumeric) return true; // If it's not a numeric column, allow all keys
        if (isNumber || allowedKeys?.includes(key)) return true;

        return false;
    };

    const handleKeyDown = (e: React.KeyboardEvent, headerId: string) => {
        const column: any = table?.getColumn(headerId);
        const numericColumn = isNumericColumn(column, headerId);

        if (!isAllowedKey(e.key, numericColumn)) {
            e.preventDefault();
        }

        if (e.key === "Enter") {
            setActiveFilterColumn(null);
        }
    };

    const getSortIcon = (header: any) => {
        const sortState = header.column.getIsSorted();
        if (sortState === "asc") return sortArrowTopIcon;
        if (sortState === "desc") return bottomarrowtbl;
        return sortIcon;
    };

    const getTableHeaderClass = (type: string) => {
        return type === "questions"
            ? "questionsetting_thtd dynamic-custom-width checkbox-dynamic-custom-width text-sm text-center"
            : "dynamic-custom-width checkbox-dynamic-custom-width text-sm text-center";
    };

    const getTableHeaderClass2 = (type: string, columnName?: any) => {
        const columnNameStr = typeof columnName === "string" ? columnName : "";

        return `tdth-custom-width dark:text-white text-sm 
      ${type === "questions" ? "questionsetting_thtd" : ""} 
      ${columnNameStr.includes("email") ? "emailcolum_width_main" : ""} 
      ${columnNameStr.includes("action") ? "actioncolum_width_main" : ""}`;
    };

    const getTableCellClass = (type: string, columnName?: any) => {
        const columnNameStr = typeof columnName === "string" ? columnName : "";

        return `relative p-4 text-sm dark:text-white tdth-custom-width 
      ${type === "questions" ? "questionsetting_thtd" : ""} 
      ${columnNameStr.includes("Email") ? "emailcolum_width_main" : ""} 
      ${columnNameStr.includes("Action") ? "actioncolum_width_main" : ""}`;
    };

    const getTableCellClass2 = (type: string) => {
        return `dynamic-custom-width text-sm items-start checkbox-dynamic-custom-width ${type === "questions" ? "questionsetting_thtd" : ""}`;
    };

    const getNotFoundMessage = () => {
        if (notFoundMessage) return notFoundMessage;
        return "No options found";
    };

    const getColSpan = () => {
        let count = selectedColumns?.length;
        if (showAllCheckbox) count += 1;
        if (serialNumber) count += 1;
        return count;
    };

    const renderDottedPagination = () => {
        if (!dottedPagination || totalPages <= 1) return null;

        return (
            <div className="self-stretch flex flex-col items-end justify-start p-4">
                <div className="flex flex-row items-center justify-end gap-[4px] mt-4 text-sm">
                    {Array.from({ length: totalPages }, (_, index) => (
                        <div
                            key={index}
                            className={`h-1.5 w-1.5 relative rounded-81xl ${currentPage === index + 1 ? "bg-colors-text-black" : "bg-surface-border"
                                } cursor-pointer`}
                            onClick={() => {
                                setSelectedRows(new Set()); // Reset selected rows when page changes
                                handlePageChange(index + 1);
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const renderButtonPagination = () => {
        if (dottedPagination) return null;

        return (
            <div className="pagination_main-div flex items-center justify-end min-w-full">
                <div className="gap-[16px] flex items-center justify-end py-2 mq575:justify-center">
                    <button
                        onClick={() => {
                            setSelectedRows(new Set());
                            handlePageChange(currentPage - 1);
                        }}
                        disabled={currentPage === 1}
                        className="cursor-pointer py-1 min-w-[35px] min-h-[35px] bg-surface-bg-primary rounded-full box-border flex flex-row items-center justify-center gap-[12px] onlybdr-darkbtn"
                        type="button"
                    >
                        <img className="h-8 w-8 relative overflow-hidden shrink-0" alt="" src={arrowLeftCircle} />
                    </button>
                    <span className="text-sm dark:text-white">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => {
                            setSelectedRows(new Set());
                            handlePageChange(currentPage + 1);
                        }}
                        disabled={currentPage === totalPages}
                        className="cursor-pointer py-1 min-w-[35px] min-h-[35px] bg-surface-bg-primary rounded-full box-border flex flex-row items-center justify-center gap-[12px] onlybdr-darkbtn"
                        type="button"
                    >
                        <img className="h-8 w-8 relative overflow-hidden shrink-0" alt="" src={arrowRightCircle} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className=" viewdoclist_tabel_blg min-w-full overflow-hidden assessorform_main_tbl client-hist-main">
            {/* min-h-[110px] */}
            <div
                className=" bg-white dark:dark:bg-slate-700 flex flex-col items-start justify-start gap-[24px] overflow-x-auto client-report-dtnamic-tbl"
                id="onboarding-tbl"
            >
                <table className="table self-stretch border-[1px] border-solid border-surface-border rounded-lg overflow-x-auto dynamictabel-main-dv">
                    <thead className="bg-surface-bg-secondary dark:bg-slate-600">
                        <tr>
                            {serialNumber && (
                                <th className={getTableCellClass(type, "")}>
                                    S.N.
                                </th>
                            )}
                            {showAllCheckbox && (
                                <th className={getTableHeaderClass(type)}>
                                    <input
                                        className="cursor-pointer "
                                        type="checkbox"
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        checked={table.getRowModel().rows.every((row) => selectedRows.has(row.id)) && table?.getRowModel()?.rows?.length > 0}
                                        draggable={false}
                                    />
                                </th>
                            )}
                            {table.getHeaderGroups().map((headerGroup) => (
                                <React.Fragment key={headerGroup.id}>
                                    {headerGroup.headers.map((header: any) => {
                                        return (
                                            <th
                                                key={header.id}
                                                draggable={isDraggable(header)}
                                                onDragStart={(e) => handleStartDrag(e, header)}
                                                onDragOver={(e) => handleDragOver(e, header)}
                                                onDrop={(e) => handleDrop(e, header.id)}
                                                className={getTableCellClass(type, flexRender(header.column.columnDef.header, header.getContext()))}
                                            >
                                                <span onClick={(e) => handleHeaderClick(e, header)} className="cursor-pointer">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </span>
                                                {header?.column?.columnDef?.enableSorting && (
                                                    <span className={`sort-icon ${header.column.getIsSorted()}`} onClick={header.column.getToggleSortingHandler()}>
                                                        <img className="h-3 w-3 cursor-pointer dark-white-img pl-[10px]" src={getSortIcon(header)} />
                                                    </span>
                                                )}

                                                {activeFilterColumn === header.id && !header?.column?.columnDef?.hideSearch && (
                                                    <div
                                                        ref={filterRef}
                                                        className="absolute text-sm left-[14px] top-[45px] mt-2 bg-white shadow-md p-2 z-50 rounded border actionalert-input-dv"
                                                    >
                                                        <input
                                                            value={filterValues[header.id] || ""}
                                                            onChange={(e) => handleFilterChange(header.id, e.target.value)}
                                                            // onKeyDown={(e) => {
                                                            //   if (e.key === "Enter") {
                                                            //     setActiveFilterColumn(null); // Close the search box
                                                            //   }
                                                            // }}

                                                            onKeyDown={(e) => handleKeyDown(e, header.id)}
                                                            placeholder="Search..."
                                                            className="w-[95px] p-2 border rounded outline-none"
                                                        />
                                                    </div>
                                                )}
                                            </th>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody     className={
        table.getRowModel()?.rows?.length === 0
            ? notFoundBodyClass || ""
            : ""
    }>
                        {table.getRowModel()?.rows?.length > 0 ? (
                            table.getRowModel()?.rows?.map((row, rowIdx) => {
                                return (
                                    <tr key={row.id}>
                                        {serialNumber && (
                                            <td align="center" className={getTableHeaderClass2(type, "")}>
                                                <span className="relative commandtbl-top top-removed-dv">
                                                    {(currentPage - 1) * (pageSize ?? 10) + rowIdx + 1}
                                                </span>
                                            </td>
                                        )}
                                        {showAllCheckbox && (
                                            <td className={getTableCellClass2(type)}>
                                                <input
                                                    className="cursor-pointer"
                                                    type="checkbox"
                                                    checked={selectedRows.has(row.id)}
                                                    onChange={() => handleRowSelect(row.id)}
                                                />
                                            </td>
                                        )}
                                        {row.getVisibleCells().map((cell: any) => {
                                            const columnId = cell?.column?.id; // Get column ID

                                            // Extract raw value from the cell
                                            const rawValue = cell?.getValue();

                                            let displayValue: string | React.ReactNode | any = renderCellContent(cell);

                                            // If rawValue is null, undefined, or an empty string, show a "-"
                                            if ((rawValue == null || rawValue === "") && cell?.column?.id != "action") {
                                                displayValue = "-";
                                            }

                                            if (columnId === "pay_rate" || columnId === "total_payroll") {
                                                const numericValue = parseFloat(rawValue); // Ensure it's numeric
                                                displayValue = !isNaN(numericValue)
                                                    ? `$${numericValue.toFixed(2)}` // Format with two decimals
                                                    : "-"; // Fallback for invalid numeric values
                                            }

                                            return (
                                                <td align="center" className={getTableHeaderClass2(type, columnId)} key={cell.id}>
                                                    <span
                                                        className={
                                                            columnId == "dosage" ? "relative commandtbl-top top-removed-dv !lowercase" : "relative commandtbl-top top-removed-dv"
                                                        }
                                                    >
                                                        {displayValue}
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={getColSpan()} className="text-left p-4 text-sm items-start flex cursor-default incidentclient-td">
                                    <span className="text-left relative max-w-[1200px]">
                                        <span className="sticky left-[40px] bottom-0 dark:text-white">{getNotFoundMessage()}</span>
                                    </span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!isAnyFilterActive && showPagination && (
                <PaginationNew
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    paginationLength={paginationDataLength}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                />
            )}
        </div>
    );
};

export default DynamicTable;
